import { xxhash64 } from './xxhash';
import {
  FEMALE_IDX,
  KOKORO_VOICES,
  MALE_IDX,
  SALT_VOICE_A,
  SALT_VOICE_B,
  SALT_SLERP_T,
  SALT_PITCH,
  SALT_ENERGY,
} from './voiceMapping';
import type { KokoroGender, VoiceSpec } from './types';

const LO32 = (1n << 32n) - 1n;

function hashName(name: string, worldSeed: bigint, salt: bigint): bigint {
  const bytes = new TextEncoder().encode(name.trim().toLowerCase());
  return xxhash64(bytes, worldSeed ^ salt);
}

function toUnitFloat(h: bigint): number {
  // Map lower 32 bits to [0, 1).
  return Number(h & LO32) / 4294967296;
}

export function genderPool(g: KokoroGender): readonly number[] {
  if (g === 'female') return FEMALE_IDX;
  if (g === 'male') return MALE_IDX;
  return KOKORO_VOICES.map((_, i) => i);
}

export interface ResolveSpecInput {
  name: string;
  gender: KokoroGender;
  worldSeed: bigint;
}

/**
 * Derive a full VoiceSpec (voiceA, voiceB, t, pitch, energy) from an agent
 * name + gender pool + world seed, using five independent xxHash64 draws.
 * Matches the desktop ZeroVoice reference's salt scheme.
 */
export function resolveKokoroSpec(input: ResolveSpecInput): VoiceSpec {
  const pool = genderPool(input.gender);
  const n = pool.length;

  const hA = hashName(input.name, input.worldSeed, SALT_VOICE_A);
  const hB = hashName(input.name, input.worldSeed, SALT_VOICE_B);
  const hT = hashName(input.name, input.worldSeed, SALT_SLERP_T);
  const hP = hashName(input.name, input.worldSeed, SALT_PITCH);
  const hE = hashName(input.name, input.worldSeed, SALT_ENERGY);

  const voiceA = pool[Number(hA % BigInt(n))];
  let voiceB: number;
  if (n === 1) {
    voiceB = voiceA;
  } else {
    const offset = Number(hB % BigInt(n - 1)); // [0, n-2]
    const voiceAIdxInPool = pool.indexOf(voiceA);
    voiceB = pool[(voiceAIdxInPool + 1 + offset) % n];
  }

  const t = toUnitFloat(hT);                   // [0.0, 1.0)
  const pitch = 0.85 + toUnitFloat(hP) * 0.30; // [0.85, 1.15)
  const energy = 0.80 + toUnitFloat(hE) * 0.40; // [0.80, 1.20)

  return { voiceA, voiceB, t, pitch, energy };
}

export function specsEqual(a: VoiceSpec, b: VoiceSpec): boolean {
  return (
    a.voiceA === b.voiceA &&
    a.voiceB === b.voiceB &&
    Math.abs(a.t - b.t) < 1e-6 &&
    Math.abs(a.pitch - b.pitch) < 1e-6 &&
    Math.abs(a.energy - b.energy) < 1e-6
  );
}

export function formatSpec(spec: VoiceSpec): string {
  const a = KOKORO_VOICES[spec.voiceA];
  const b = KOKORO_VOICES[spec.voiceB];
  if (spec.voiceA === spec.voiceB) {
    return `${a} (self, p=${spec.pitch.toFixed(2)} e=${spec.energy.toFixed(2)})`;
  }
  return `${a} × ${b} @ ${spec.t.toFixed(3)}, p=${spec.pitch.toFixed(2)} e=${spec.energy.toFixed(2)}`;
}
